import LegalLayout from "@/pages/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" effective="March 2, 2026" testId="privacy-page">
      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          1. Introduction
        </h2>
        <p>
          Protego AI Inc. (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates the
          website section3.ai and provides the Protego AI account health monitoring platform
          (collectively, the &quot;Service&quot;). This Privacy Policy explains how we
          collect, use, disclose, and safeguard your information when you use our Service.
          Please read this policy carefully. By accessing or using our Service, you
          acknowledge that you have read and understood this Privacy Policy.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          2. Information We Collect
        </h2>

        <h3 className="text-xl font-bold text-white mt-6 mb-3">2.1 Information You Provide</h3>
        <p>We may collect information you voluntarily provide when you:</p>
        <ul className="list-disc pl-6 space-y-2 mt-3 text-white/75">
          <li>Register for an account or request a demo</li>
          <li>Complete lead capture forms at trade shows or on our website</li>
          <li>Contact us by email or through our contact forms</li>
          <li>Subscribe to our newsletter or educational content</li>
        </ul>
        <p className="mt-4">
          This may include your name, email address, company name, Amazon Seller ID, phone
          number, and any other information you choose to provide.
        </p>

        <h3 className="text-xl font-bold text-white mt-6 mb-3">
          2.2 Amazon Selling Partner API Data
        </h3>
        <p>
          To provide our account health monitoring service, we request your authorization to
          access your Amazon seller data through the Amazon Selling Partner API (SP-API). This
          may include order performance metrics, account health ratings, policy warnings,
          inventory data, customer feedback metrics, and other data Amazon makes available
          through the SP-API. We only access the data necessary to deliver the Service.
        </p>

        <h3 className="text-xl font-bold text-white mt-6 mb-3">
          2.3 Automatically Collected Information
        </h3>
        <p>
          When you visit our website, we may automatically collect certain information,
          including your IP address, browser type and version, operating system, pages
          visited, time spent on pages, referring URLs, and device information.
        </p>

        <h3 className="text-xl font-bold text-white mt-6 mb-3">
          2.4 Cookies and Tracking Technologies
        </h3>
        <p>
          We use cookies and similar tracking technologies to improve your experience on our
          website, analyze usage patterns, and deliver relevant content. You may control
          cookie settings through your browser preferences, though disabling cookies may
          affect the functionality of our website.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          3. How We Use Your Information
        </h2>
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-6 space-y-2 mt-3 text-white/75">
          <li>Provide, operate, and improve the Service</li>
          <li>Analyze your Amazon account health data and generate risk reports and alerts</li>
          <li>Communicate with you about your account, updates, and promotional content</li>
          <li>Respond to your inquiries and provide customer support</li>
          <li>Conduct research and analysis to improve our platform and services</li>
          <li>Send you educational content about Amazon account health and policy enforcement</li>
          <li>Comply with legal obligations and enforce our Terms of Service</li>
          <li>Detect and prevent fraud, abuse, and security incidents</li>
        </ul>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          4. How We Share Your Information
        </h2>
        <p>
          We do not sell, rent, or trade your personal information to third parties. We may
          share your information in the following circumstances:
        </p>

        <h3 className="text-xl font-bold text-white mt-6 mb-3">4.1 Service Providers</h3>
        <p>
          We may share your information with trusted third-party service providers who assist
          us in operating our business, including cloud hosting providers, analytics services,
          email delivery services, and customer support tools. These providers are
          contractually obligated to protect your information and may only use it to perform
          services on our behalf.
        </p>

        <h3 className="text-xl font-bold text-white mt-6 mb-3">4.2 Business Transfers</h3>
        <p>
          If we are involved in a merger, acquisition, or sale of all or a portion of our
          assets, your information may be transferred as part of that transaction. We will
          notify you via email or prominent notice on our website of any change in ownership
          or use of your information.
        </p>

        <h3 className="text-xl font-bold text-white mt-6 mb-3">4.3 Legal Requirements</h3>
        <p>
          We may disclose your information if required to do so by law or in response to valid
          requests by public authorities (e.g., a court or government agency).
        </p>

        <h3 className="text-xl font-bold text-white mt-6 mb-3">4.4 Protection of Rights</h3>
        <p>
          We may disclose your information where we believe it is necessary to investigate,
          prevent, or take action regarding potential violations of our policies, suspected
          fraud, situations involving potential threats to safety, or as evidence in
          litigation.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          5. Data Retention
        </h2>
        <p>
          We retain your personal information for as long as necessary to provide the Service,
          fulfill the purposes outlined in this Privacy Policy, and comply with legal
          obligations. Amazon SP-API data is retained for the duration of your active account
          and for up to 12 months following account termination, unless a shorter retention
          period is required by Amazon&apos;s data usage policies or applicable law. You may
          request deletion of your data at any time by contacting us at{" "}
          <a
            href="mailto:hello@protegoos.com"
            className="text-[#F04632] font-semibold hover:underline"
          >
            hello@protegoos.com
          </a>
          .
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          6. Data Security
        </h2>
        <p>
          We implement appropriate technical and organizational security measures to protect
          your information against unauthorized access, alteration, disclosure, or
          destruction. However, no method of transmission over the Internet or method of
          electronic storage is 100% secure. While we strive to use commercially acceptable
          means to protect your information, we cannot guarantee absolute security.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          7. Your Rights and Choices
        </h2>
        <p>
          Depending on your location, you may have the following rights regarding your
          personal information:
        </p>
        <ul className="list-disc pl-6 space-y-2 mt-3 text-white/75">
          <li>
            <strong className="text-white">Access:</strong> Request a copy of the personal
            information we hold about you
          </li>
          <li>
            <strong className="text-white">Correction:</strong> Request correction of
            inaccurate or incomplete data
          </li>
          <li>
            <strong className="text-white">Deletion:</strong> Request deletion of your
            personal information
          </li>
          <li>
            <strong className="text-white">Portability:</strong> Request transfer of your
            data in a structured, machine-readable format
          </li>
          <li>
            <strong className="text-white">Objection:</strong> Object to certain processing
            activities, including marketing communications
          </li>
          <li>
            <strong className="text-white">Withdrawal of Consent:</strong> Withdraw consent
            for SP-API access at any time through your Amazon Seller Central settings
          </li>
        </ul>
        <p className="mt-4">
          To exercise any of these rights, please contact us at{" "}
          <a
            href="mailto:hello@protegoos.com"
            className="text-[#F04632] font-semibold hover:underline"
          >
            hello@protegoos.com
          </a>
          . We will respond to your request within 30 days.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          8. Children&apos;s Privacy
        </h2>
        <p>
          Our Service is not directed to individuals under the age of 18. We do not knowingly
          collect personal information from children. If we become aware that we have
          collected personal information from a child under 18, we will take steps to delete
          such information promptly.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          9. Third-Party Links
        </h2>
        <p>
          Our website may contain links to third-party websites. We are not responsible for
          the privacy practices or content of those sites. We encourage you to review the
          privacy policies of any third-party websites you visit.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          10. Amazon&apos;s Policies
        </h2>
        <p>
          Our collection and use of data obtained through the Amazon SP-API is subject to
          Amazon&apos;s Selling Partner API Developer Agreement and data protection policies.
          We comply with all applicable Amazon policies regarding the use and storage of
          seller data.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          11. Changes to This Privacy Policy
        </h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any
          material changes by posting the new policy on this page with an updated effective
          date. Your continued use of the Service after such changes constitutes your
          acceptance of the updated policy.
        </p>
      </section>

      <section>
        <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white mt-10 mb-4">
          12. Contact Us
        </h2>
        <p>
          If you have questions or concerns about this Privacy Policy or our data practices,
          please contact us:
        </p>
        <div className="mt-4 bg-[#121316] border border-white/10 rounded-xl p-5">
          <p className="font-bold text-white mb-1">Protego AI Inc.</p>
          <p>
            Email:{" "}
            <a
              href="mailto:hello@protegoos.com"
              className="text-[#F04632] font-semibold hover:underline"
            >
              hello@protegoos.com
            </a>
          </p>
          <p>
            Website:{" "}
            <a
              href="https://section3.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#F04632] font-semibold hover:underline"
            >
              https://section3.ai
            </a>
          </p>
        </div>
      </section>
    </LegalLayout>
  );
}
